import { useEffect, useRef, useState } from 'react';
import { getEditorMedia, putEditorMedia } from '../../../utils/editorMediaStore';

export interface ThumbnailRequest {
  /** Identifies the media; its thumbnail is made once and shared by every list that shows it. */
  key: string;
  /** Makes the thumbnail picture. Only called when no kept copy exists. */
  make: () => Promise<Blob>;
  /** Keep the picture in this browser so it is there at once next time. */
  keep: boolean;
}

/** How many thumbnails are made at the same time; the rest wait their turn. */
const PARALLEL = 2;

const storeKey = (key: string) => `thumb:${key}`;

/**
 * Thumbnail pictures for a list of media, by key. Each is made once, a couple at a time
 * and in list order, and a picture that cannot be made is left out (the list shows its
 * icon instead). When `retryKey` changes, pictures that could not be made are tried
 * again: pass something that changes when making them may now succeed.
 */
export const useMediaThumbnails = (requests: ThumbnailRequest[], retryKey?: unknown): Record<string, string> => {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const started = useRef(new Set<string>());
  const failed = useRef(new Set<string>());
  const waiting = useRef<ThumbnailRequest[]>([]);
  const latest = useRef(new Map<string, ThumbnailRequest>());
  const running = useRef(0);
  const generation = useRef(0);
  const lastRetryKey = useRef(retryKey);
  const objectUrls = useRef<string[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const urls = objectUrls.current;
    return () => {
      mounted.current = false;
      // Requests that never got their turn may be asked for again (React re-runs effects in development)
      for (const request of waiting.current) started.current.delete(request.key);
      waiting.current = [];
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    latest.current = new Map(requests.map(request => [request.key, request]));
    if (!Object.is(lastRetryKey.current, retryKey)) {
      lastRetryKey.current = retryKey;
      generation.current++;
      for (const key of failed.current) started.current.delete(key);
      failed.current.clear();
    }

    const pump = () => {
      while (mounted.current && running.current < PARALLEL && waiting.current.length > 0) {
        const request = waiting.current.shift()!;
        const startedIn = generation.current;
        running.current++;
        void (async () => {
          try {
            const kept = request.keep ? await getEditorMedia(storeKey(request.key)).catch(() => null) : null;
            const blob = kept ?? await request.make();
            if (!kept && request.keep) putEditorMedia(storeKey(request.key), blob).catch(() => undefined);
            if (!mounted.current) return;
            const url = URL.createObjectURL(blob);
            objectUrls.current.push(url);
            setThumbnails(prev => ({ ...prev, [request.key]: url }));
          } catch {
            // Conditions changed while it was being made: try again with the current request
            const again = startedIn !== generation.current ? latest.current.get(request.key) : undefined;
            if (again) waiting.current.push(again);
            else failed.current.add(request.key);
          } finally {
            running.current--;
            pump();
          }
        })();
      }
    };
    for (const request of requests) {
      if (started.current.has(request.key)) continue;
      started.current.add(request.key);
      waiting.current.push(request);
    }
    pump();
  }, [requests, retryKey]);

  return thumbnails;
};
